import React, { useState } from "react";
import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { Constants } from "modules/root/models/constants";
import { useMst } from "modules/root/models/hooks";
import styles from "./PushBanner.module.scss";

/** Тот же ключ, что у мобильной версии: «Позже» на одном экране скрывает плашку и там (specs/006 R9) */
export const PUSH_SNOOZE_KEY = "rost.push.snoozedUntil";

const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

const readSnooze = (): number => {
    try {
        return Number(localStorage.getItem(PUSH_SNOOZE_KEY)) || 0;
    } catch {
        return 0;
    }
};

const permissionNow = (): NotificationPermission | null =>
    typeof window !== "undefined" && "Notification" in window && "PushManager" in window
        ? window.Notification.permission
        : null;

/**
 * Плашка «Включите уведомления» на Главной полной версии (specs/006 US7, FR-031): системный
 * запрос разрешения — только по «Включить»; «Позже» — на 14 дней; при запрете — как разрешить в
 * браузере. Без push на сервере (пустой `pushPublicKey`) и без поддержки браузером не видна.
 */
export const PushBanner = observer(() => {
    const store = useMst();
    const [permission, setPermission] = useState(permissionNow);
    const [snoozedUntil, setSnoozedUntil] = useState(readSnooze);

    if (!Constants.pushPublicKey || permission === null || permission === "granted" || Date.now() < snoozedUntil) {
        return null;
    }

    const later = () => {
        const until = Date.now() + SNOOZE_MS;

        try {
            localStorage.setItem(PUSH_SNOOZE_KEY, String(until));
        } catch {
            // хранилище недоступно — скроем до перезагрузки
        }

        setSnoozedUntil(until);
    };

    return (
        <div className={styles.banner} role="region" aria-label="Уведомления">
            {permission === "denied" ? (
                <span className={styles.text}>
                    Уведомления запрещены в браузере. Чтобы включить, нажмите значок замка в адресной строке →
                    Уведомления → Разрешить и обновите страницу
                </span>
            ) : (
                <span className={styles.text}>
                    Включите уведомления: новые запросы, задания и напоминания придут, даже когда Rost закрыт
                </span>
            )}
            <div className={styles.actions}>
                <PlanrButton type="graybtn" size="small" onClick={later}>
                    Позже
                </PlanrButton>
                {permission === "default" && (
                    <PlanrButton
                        type="primary"
                        size="small"
                        onClick={async () => setPermission(await store.enablePush())}
                    >
                        Включить
                    </PlanrButton>
                )}
            </div>
        </div>
    );
});
